/**
 * novaLaunches — where each projectile of a nova / fan leaves and how fast
 * (plan 29 F5). NovaProjectileBehavior launches the caster's knives from it;
 * the observer launches the same visual knives (no damage) from the cast's
 * geometry, so Fan of Knives reads on both screens.
 */
import { PROJECTILE_VFX, shapeAspect } from '../../../shared/config/projectile-vfx.config';

/** A nova's resolved look: count, arc, speed and the projectile box. */
export interface NovaLook {
  count: number;
  arcDegrees: number;
  speed: number;
  shape: string;
  color: number;
  width: number;
  height: number;
  lifespan: number;
  gravity: number;
}

/** Resolve a nova config against the ability's projectile metadata (the behaviour's own fallbacks). */
export function resolveNovaLook(
  c: { count: number; arcDegrees: number; speed?: number; shape?: string; color?: number; width?: number; height?: number; lifespan?: number; gravity?: number },
  projectile?: { shape?: string; color?: number; width?: number; height?: number },
): NovaLook {
  const shape = c.shape ?? projectile?.shape ?? 'rect';
  const width = c.width ?? projectile?.width ?? PROJECTILE_VFX.multi.width;
  return {
    count: c.count,
    arcDegrees: c.arcDegrees,
    speed: c.speed ?? 600,
    shape,
    color: c.color ?? projectile?.color ?? 0xFFFFFF,
    width,
    height: c.height ?? projectile?.height ?? Math.round(width * shapeAspect(shape)),
    lifespan: c.lifespan ?? 800,
    gravity: c.gravity ?? 0,
  };
}

export interface NovaLaunch { x: number; y: number; vx: number; vy: number; isRight: boolean }

/**
 * Each projectile's top-left spawn and velocity: centred on the body (plan 29
 * F7), evenly over [0, 2π) for a full circle, else from −arc/2 to +arc/2
 * around the facing.
 */
export function novaLaunches(look: Pick<NovaLook, 'count' | 'arcDegrees' | 'speed' | 'width' | 'height'>, body: { x: number; y: number }, facingRight: boolean): NovaLaunch[] {
  const baseAngle = facingRight ? 0 : Math.PI;
  const halfArc = (look.arcDegrees * Math.PI) / 180 / 2;
  const isFullCircle = look.arcDegrees >= 360;
  const x = body.x - look.width / 2;
  const y = body.y - look.height / 2;
  const out: NovaLaunch[] = [];
  for (let i = 0; i < look.count; i++) {
    let angle: number;
    if (isFullCircle) angle = baseAngle + (look.count > 0 ? (i / look.count) * 2 * Math.PI : 0);
    else angle = baseAngle + (look.count === 1 ? 0 : (i / (look.count - 1)) * 2 - 1) * halfArc;
    const vx = Math.cos(angle) * look.speed;
    out.push({ x, y, vx, vy: Math.sin(angle) * look.speed, isRight: vx >= 0 });
  }
  return out;
}
